import { useMemo } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { mainNavItems, NavItem } from '@/components/layout/navigation/navItems';
import { HREF_TO_MODULE_MAP } from '@/components/layout/navigation/utils';

/**
 * Hook to filter navigation items based on user permissions
 * Platform admins bypass all permission checks and see full navigation
 */
export function useFilteredNavigation(organizationId?: string) {
  const { canAccessModule, isLoading } = usePermissions(organizationId);
  const { data: platformAdmin, isLoading: adminLoading } = usePlatformAdmin();

  const filteredMainNavItems = useMemo(() => {
    if (isLoading || adminLoading) return [];

    // ✅ Platform admins see ALL navigation items without restrictions
    if (platformAdmin?.isAdmin) {
      return mainNavItems;
    }

    // Regular users see navigation filtered by their RBAC permissions
    return mainNavItems.filter((item: NavItem) => {
      const module = HREF_TO_MODULE_MAP[item.href];
      if (!module) return true; // Show items without module mapping
      return canAccessModule(module);
    });
  }, [canAccessModule, platformAdmin, isLoading, adminLoading]);

  return {
    filteredMainNavItems,
    isLoading: isLoading || adminLoading,
    isPlatformAdmin: platformAdmin?.isAdmin || false,
    platformAdminRole: platformAdmin?.adminRole || null
  };
}
